<template>
  <div class="subscription-compose-form">
    <v-flex v-if="loading" class="pb-16">
      <span>{{ adminText('workflowsAll.loading_subscribers_0ff2de6', 'Loading subscribers…') }}</span>
    </v-flex>
    <v-flex v-if="loadError" class="pb-16" role="alert">
      <span class="skipped-note">{{ workflowLabel(loadError) }}</span>
    </v-flex>
    <v-flex class="pb-16">
      <text-field
        v-model.trim="form.replyTo"
        :validator="vs.form.replyTo"
        :label="adminText('workflowsAll.reply_to_e_mail_aed12b6', 'Reply to e-mail*')"
      />
    </v-flex>
    <v-flex class="pb-16">
      <select-field
        v-model="form.subscriptionPlan"
        :items="localizedSubscriptionPlans"
        item-select="id"
        item-render="name"
        :label="adminText('workflowsAll.subscription_plans_96663a3', 'Subscription plans')"
      />
    </v-flex>
    <v-flex class="pb-16">
      <select-field
        v-model="form.season"
        :items="workflowUi(seasonOptions)"
        item-select="value"
        item-render="text"
        :label="adminText('workflowsAll.season_62255f5', 'Season')"
      />
    </v-flex>
    <v-flex class="pb-16">
      <select-field
        v-model="form.eventId"
        :items="localizedEventOptions"
        item-select="id"
        item-render="name"
        :label="adminText('workflowsAll.event_ad8919a', 'Event')"
      />
    </v-flex>
    <v-flex class="pb-16">
      <select-field
        v-model="form.recipients"
        :validator="vs.form.recipients"
        :items="localizedRecipients"
        item-render="caption"
        item-select="value"
        :label="adminText('workflowsAll.recipients_faf993d', 'Recipients*')"
      />
    </v-flex>
    <v-flex class="pb-16">
      <autocomplete-field
        v-if="form.recipients === 'Specific subscribers'"
        v-model="form.attendees"
        :items="workflowUi(subscriberOptions)"
        item-select="id"
        item-render="name"
        :label="adminText('workflowsAll.subscribers_75ecd90', 'Subscribers*')"
        chips
        multiple
      />
    </v-flex>
    <v-flex v-if="skippedCount" class="pb-16">
      <span class="skipped-note">
        {{ workflowLabel(skippedCount) }} {{ adminText('workflowsAll.subscriber_s_have_no_e_mail_address_and_canno_d531c6d', 'subscriber(s) have no e-mail address and cannot be messaged.') }}
      </span>
    </v-flex>
    <v-flex class="pb-16">
      <text-field v-model.trim="form.subject" :validator="vs.form.subject" :label="adminText('workflowsAll.subject_54fc0ac', 'Subject*')" />
    </v-flex>
    <div class="editor-row">
      <v-flex class="editor-field pb-16">
        <text-editor-field
          v-model.trim="form.content"
          :validator="vs.form.content"
          :placeholder="adminText('workflowsAll.description_55f8ebc', 'Description')"
          style="margin: 0"
        />
      </v-flex>
      <div class="shortcodes-panel"><slot name="shortcodes" /></div>
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { getTheaterSubscriptionPurchases, getTheaterSubscriptionEmailPlans } from '@/graphql/theater-subscriptions.graphql.js'

export default {
  mixins: [WorkflowDisplay],
  name: 'create-theater-subscription-email-form',
  props: {
    vs: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    organizationSlug: {
      type: String,
      required: true,
    },
    preselectedPurchaseId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      purchases: [],
      recipients: ['All subscribers', 'Specific subscribers'],
      loading: false,
      loadError: null,
    }
  },
  computed: {
    localizedEventOptions() { return this.eventOptions.map(event => event.id === null ? { ...event, name: this.workflowEventOptionLabel(event) } : event) },
    localizedRecipients() { return this.recipients.map(value => ({ value, caption: this.workflowRecipientOption(value) })) },
    localizedSubscriptionPlans() {
      return this.subscriptionPlans.map(plan => plan.id === null ? { ...plan, name: this.workflowPlanOptionLabel(plan) } : plan)
    },
    seasonOptions() {
      const seasons = [...new Set(this.purchases.map(p => this.getSeasonLabel(p.theaterSubscription)).filter(Boolean))]

      return [{ value: '', text: 'workflowsAll.all_seasons_5666605' }, ...seasons.sort().reverse().map(value => ({ value, text: value }))]
    },
    seasonPurchases() {
      return this.purchases.filter(p => !this.form.season || this.getSeasonLabel(p.theaterSubscription) === this.form.season)
    },
    // Only purchases that are still active and actually have an address to send to.
    messageablePurchases() {
      return this.seasonPurchases.filter(
        purchase => purchase.active && !!String(purchase.customerEmail || '').trim(),
      )
    },
    subscriptionPlans() {
      const plans = new Map()
      this.seasonPurchases.filter(purchase => purchase.active).forEach(purchase => {
        if (purchase.theaterSubscription) {
          plans.set(purchase.theaterSubscription.id, purchase.theaterSubscription)
        }
      })

      return [{ id: null, name: 'All plans' }, ...Array.from(plans.values()).sort((a, b) => a.name.localeCompare(b.name))]
    },
    eventOptions() {
      const events = new Map()
      this.seasonPurchases
        .filter(purchase => purchase.active && purchase.theaterSubscription &&
          (!this.form.subscriptionPlan || Number(purchase.theaterSubscription.id) === Number(this.form.subscriptionPlan)))
        .forEach(purchase => {
          const links = purchase.theaterSubscription && purchase.theaterSubscription.theaterSubscriptionEvents || []
          links.forEach(link => {
            if (!link.event) return
            const date = link.event.from ? new Date(link.event.from) : null
            const dateLabel = date && !Number.isNaN(date.getTime())
              ? ` — ${date.toLocaleDateString('en-GB', { timeZone: 'Europe/Zagreb', day: '2-digit', month: '2-digit', year: 'numeric' })}`
              : ''
            events.set(Number(link.eventId), { id: Number(link.eventId), name: `${link.event.name}${dateLabel}` })
          })
        })

      return [{ id: null, name: 'All events' }, ...Array.from(events.values()).sort((a, b) => a.name.localeCompare(b.name))]
    },
    scopedPurchases() {
      return this.messageablePurchases.filter(
        purchase =>
          purchase.theaterSubscription &&
          (!this.form.subscriptionPlan || Number(purchase.theaterSubscription.id) === Number(this.form.subscriptionPlan)) &&
          (!this.form.eventId || (purchase.theaterSubscription.theaterSubscriptionEvents || [])
            .some(link => Number(link.eventId) === Number(this.form.eventId))),
      )
    },
    subscriberOptions() {
      return this.scopedPurchases.map(purchase => ({
        id: purchase.id,
        name: `${purchase.customerName} (${purchase.customerEmail})`,
      }))
    },
    skippedCount() {
      const inScope = this.seasonPurchases.filter(purchase => {
        if (!purchase.active) return false

        return (
          purchase.theaterSubscription &&
          (!this.form.subscriptionPlan || Number(purchase.theaterSubscription.id) === Number(this.form.subscriptionPlan)) &&
          (!this.form.eventId || (purchase.theaterSubscription.theaterSubscriptionEvents || [])
            .some(link => Number(link.eventId) === Number(this.form.eventId)))
        )
      })

      const selectedIds = new Set((this.form.attendees || []).map(attendee => String(attendee.id || attendee)))

      return inScope.filter(purchase =>
        (this.form.recipients !== 'Specific subscribers' || selectedIds.has(String(purchase.id))) &&
        !String(purchase.customerEmail || '').trim(),
      ).length
    },
  },
  watch: {
    'form.season': function () {
      if (this.form.subscriptionPlan && !this.subscriptionPlans.some(p => Number(p.id) === Number(this.form.subscriptionPlan))) {
        this.form.subscriptionPlan = null
      }
      this.clearUnavailableEvent()
      if (this.form.recipients === 'Specific subscribers') this.updateAttendees([])
      else this.syncAllRecipients()
    },
    // "All subscribers" is resolved to explicit ids, so narrowing the plan must re-resolve them.
    scopedPurchases() {
      this.syncAllRecipients()
    },
    'form.recipients': function () {
      if (this.form.recipients === 'Specific subscribers') {
        this.updateAttendees([])
      } else {
        this.syncAllRecipients()
      }
    },
    'form.subscriptionPlan': function () {
      this.clearUnavailableEvent()
      if (this.form.recipients === 'Specific subscribers') {
        this.updateAttendees([])
      }
    },
    'form.eventId': function () {
      if (this.form.recipients === 'Specific subscribers') this.updateAttendees([])
      else this.syncAllRecipients()
    },
  },
  async created() {
    this.loading = true
    try {
      await this.fetchPurchases()
      if (this.preselectedPurchaseId && this.form.recipients === 'Specific subscribers') {
        const match = this.scopedPurchases.find(purchase => Number(purchase.id) === Number(this.preselectedPurchaseId))
        if (match) this.updateAttendees([match.id])
        else this.loadError = 'workflowsAll.the_selected_subscriber_is_unavailable_for_me_6add57a'
      } else this.syncAllRecipients()
    } catch (error) {
      this.purchases = []
      this.loadError = 'workflowsAll.subscribers_could_not_be_loaded_refresh_the_p_5046800'
    } finally {
      this.loading = false
    }
  },
  methods: {
    getSeasonLabel(plan) {
      if (!plan || !plan.valid_from) return ''
      const from = new Date(plan.valid_from).getFullYear()
      if (Number.isNaN(from)) return ''
      const to = plan.valid_to ? new Date(plan.valid_to).getFullYear() : from

      return Number.isNaN(to) || from === to ? `${from}` : `${from}/${to}`
    },
    async fetchPurchases() {
      const variables = { organizationSlug: this.organizationSlug }
      const [purchasesResult, plansResult] = await Promise.all([
        this.$apollo.query({ query: getTheaterSubscriptionPurchases, variables, fetchPolicy: 'no-cache' }),
        this.$apollo.query({ query: getTheaterSubscriptionEmailPlans, variables, fetchPolicy: 'no-cache' }),
      ])
      const purchases = purchasesResult.data && purchasesResult.data.getTheaterSubscriptionPurchases
      const plans = plansResult.data && plansResult.data.getTheaterSubscriptions
      if (!Array.isArray(purchases) || !Array.isArray(plans)) throw new Error('Subscriber email scope is incomplete')
      const eventsByPlan = new Map(plans.map(plan => [Number(plan.id), plan.theaterSubscriptionEvents || []]))
      if (purchases.some(purchase => purchase.active && String(purchase.customerEmail || '').trim() &&
        (!purchase.theaterSubscription || !eventsByPlan.has(Number(purchase.theaterSubscription.id))))) {
        throw new Error('Subscriber email plans are incomplete')
      }
      this.purchases = purchases.map(purchase => ({
        ...purchase,
        theaterSubscription: purchase.theaterSubscription && {
          ...purchase.theaterSubscription,
          theaterSubscriptionEvents: eventsByPlan.get(Number(purchase.theaterSubscription.id)),
        },
      }))
    },
    updateAttendees(attendees) {
      const current = this.form.attendees || []
      // Replacing the parent form re-evaluates scopedPurchases. Do not emit
      // unchanged recipients back to the parent and start another update cycle.
      if (current.length === attendees.length && current.every((id, index) => id === attendees[index])) {
        return
      }
      this.$emit('values', {
        ...this.form,
        attendees,
      })
    },
    clearUnavailableEvent() {
      if (this.form.eventId && !this.eventOptions.some(event => event.id === Number(this.form.eventId))) {
        this.form.eventId = null
      }
    },
    syncAllRecipients() {
      if (this.form.recipients !== 'All subscribers') {
        return
      }
      this.updateAttendees(this.scopedPurchases.map(purchase => purchase.id))
    },
  },
}
</script>

<style scoped>
.editor-row {
  display: flex;
  align-items: flex-start;
  gap: 18px;
}
.editor-field { flex: 1 1 auto; min-width: 0; }
.editor-field >>> .ql-container {
  min-height: 220px;
  border: 1px solid #d9d9d9 !important;
  border-radius: 0 0 4px 4px;
}
.editor-field >>> .ql-editor {
  min-height: 220px;
  border-bottom: 0 !important;
}
.editor-field >>> .custom-error .ql-container {
  border-color: #db384c !important;
}
.shortcodes-panel { flex: 0 0 330px; min-width: 0; }
.shortcodes-panel >>> .card-container {
  padding: 16px;
  border: 1px solid #f5f5f5;
  border-radius: 8px;
}
.shortcodes-panel >>> .shortcode-container {
  margin-left: 0;
  margin-right: 8px;
  background: #e6f1f8;
  border-radius: 8px;
  height: auto;
}
.subscription-compose-form >>> .v-text-field--box > .v-input__control > .v-input__slot {
  background: white !important;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-shadow: none !important;
  min-height: 56px;
}
.subscription-compose-form >>> .v-text-field--box > .v-input__control > .v-input__slot::before,
.subscription-compose-form >>> .v-text-field--box > .v-input__control > .v-input__slot::after {
  display: none;
}
.subscription-compose-form >>> .v-input--has-state .v-input__slot {
  border-color: #db384c !important;
}
@media (max-width: 1050px) {
  .editor-row { flex-wrap: wrap; }
  .editor-field, .shortcodes-panel { flex-basis: 100%; }
}
.skipped-note {
  color: #db384c;
  font-size: 14px;
}
</style>
